/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableWithin(panel: HTMLElement): HTMLElement[] {
  return [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
}

/**
 * Dialog focus contract: on open, focus moves to the first focusable element in the panel unless
 * the panel already holds focus (an `autoFocus` child wins); Tab and Shift+Tab cycle inside the
 * panel; on close, focus returns to whatever opened the dialog. The opener is remembered while the
 * dialog is closed, because React applies a child's `autoFocus` during commit, before any effect
 * runs, so reading `document.activeElement` on open already finds focus inside the panel. The
 * tracker is a layout effect so its cleanup runs in the mutation phase, ahead of that `autoFocus`;
 * a passive effect would still be listening and record the dialog's own input as the opener.
 */
export function useDialogFocus(panelRef: RefObject<HTMLElement | null>, isOpen: boolean): void {
  const openerRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (isOpen) return;
    const remember = () => {
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    };
    remember();
    document.addEventListener("focusin", remember);
    return () => document.removeEventListener("focusin", remember);
  }, [isOpen]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!isOpen || !panel) return;

    const opener = openerRef.current;
    if (!panel.contains(document.activeElement)) {
      (focusableWithin(panel)[0] ?? panel).focus();
    }

    const confineTab = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const elements = focusableWithin(panel);
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      const current = document.activeElement;
      if (event.shiftKey && (current === first || current === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    panel.addEventListener("keydown", confineTab);
    return () => {
      panel.removeEventListener("keydown", confineTab);
      opener?.focus();
    };
  }, [panelRef, isOpen]);
}
